// monitoringSnapshotAdapter — single isolation point for sun's consolidated
// monitoring snapshot (sun-contract-proposal.md section 6.1, built by sun in
// PR sun#3522): ONE call per tick replacing deviceSummary + deviceGroupErrors +
// plantKpis (+ the per-card deviceinfo N+1, embedded as `liveInfo`).
//
// Change detection is stateless on sun's side: the client echoes back the opaque
// `version` token from the last full response (`treeHash` or
// `treeHash:progressToken`); when nothing changed sun answers
// `200 {"unchanged":true,"version":"..."}` and the tick costs almost nothing.
// `partProgress` travels only when `partId` is sent AND its progress segment
// changed; when sun omits the field the client keeps its current route (this
// adapter surfaces that as `partProgress: undefined`).
//
// NOTE (contract §6 addendum, requested 2026-07-08): the snapshot MAY embed
// `physicalLocations` (the `fixtureLocations/actual` payload) under the same treeHash.
// When present, the screen consumes it and drops the standalone call (1 call/tick);
// when absent (pre-embed sun) the screen still fetches `fixtureLocations/actual`
// alongside. This adapter surfaces it as `physicalLocations` (or `undefined`).
import { apiGet } from '../generic/Api_Functions'

const SNAPSHOT_ENDPOINT = 'monitoring/snapshot'

/** Sun sends `groupType` as a plain string in the snapshot (contract deviation);
 * components read `groupType?.name` (legacy shape), so normalise here. */
function normalizeGroupType (groupType) {
  if (typeof groupType === 'string') return { name: groupType }
  return groupType ?? {}
}

/**
 * Maps the snapshot tree to the legacy flat `deviceSummary` array shape that
 * `plantMonitoringUtils`/components already consume (groups with `type: 'GROUP'`
 * and `devices[]`, then ungrouped devices) — so NO component changes are needed.
 */
function mapSnapshotTree (snapshot) {
  const groups = (snapshot.deviceGroups ?? []).map((group) => ({
    ...group,
    type: 'GROUP',
    groupType: normalizeGroupType(group.groupType)
  }))
  return [...groups, ...(snapshot.ungroupedDevices ?? [])]
}

/**
 * Fetches sun's consolidated monitoring snapshot.
 * @param {{userId: string|number, version?: string, partId?: string|number}} params
 * @returns {Promise<
 *   {degraded: true} |
 *   {unchanged: true, version: string} |
 *   {version: string, devices: Array, sensorErrors: Array, plantKpis: Object, partProgress?: Array, physicalLocations?: Array}
 * >} `degraded` on network error or unrecognisable payload (callers fall back to
 * the legacy per-endpoint path); `partProgress` is `undefined` when sun omitted it
 * (meaning: keep the route you already have).
 */
async function fetchMonitoringSnapshot ({ userId, version = '', partId = '' }) {
  try {
    const params = new URLSearchParams()
    if (version !== '') params.set('version', version)
    if (partId !== '') params.set('partId', String(partId))
    const query = params.toString()
    const response = await apiGet(SNAPSHOT_ENDPOINT + '/' + userId + (query !== '' ? '?' + query : ''))

    if (response?.unchanged === true) {
      return { unchanged: true, version: response.version ?? version }
    }
    if (!response || typeof response.version !== 'string' || !Array.isArray(response.deviceGroups ?? [])) {
      return { degraded: true }
    }
    if (response.deviceGroups === undefined && response.ungroupedDevices === undefined) {
      // No tree at all: not the contract shape — treat as an older/foreign sun.
      return { degraded: true }
    }
    return {
      version: response.version,
      devices: mapSnapshotTree(response),
      sensorErrors: response.sensorErrors ?? [],
      plantKpis: { ...(response.plantKpis ?? {}), degraded: response.plantKpis === undefined },
      partProgress: Array.isArray(response.partProgress) ? response.partProgress : undefined,
      // Optional embed (contract §6 addendum): fixtureLocations/actual under the same
      // token. `undefined` when sun doesn't embed it yet (screen fetches it separately).
      physicalLocations: Array.isArray(response.physicalLocations) ? response.physicalLocations : undefined
    }
  } catch (error) {
    return { degraded: true }
  }
}

export { SNAPSHOT_ENDPOINT, fetchMonitoringSnapshot, mapSnapshotTree }
